import React, {useEffect, useMemo} from "react";
import {useAuthState} from "react-firebase-hooks/auth";

import {
  GameServer,
  LivePlayerController,
  Player,
  ReplayVerifier,
  World,
} from "@repo/shared";

import {submitReplayV2} from "~/api/hooks/useSubmitReplayV2";
import {useGlobalEventDispatch} from "~/contexts/event/GlobalEventBusContext";
import {useGame} from "~/contexts/game-manager/GameManagerProvider";
import {useServerTime} from "~/contexts/time/ServerTimeProvider";
import {auth} from "~/firebase/firebase";

import {
  SetTabbingPayload,
  useUserInput,
} from "../../contexts/user-input/UserInputCoordinatorProvider";

const COUNTDOWN_TIME_MS = 500;

/**
 * A self-contained component that sets up and manages a single-player game
 * with THREE locally controlled players.
 */
const SinglePlayerGame: React.FC<{levelId: string; world: World}> = ({
  levelId,
  world,
}) => {
  const {gameManager} = useGame();
  const getEstimatedServerTime = useServerTime();
  const {emitter: userInputEmitter} = useUserInput();
  const [user] = useAuthState(auth);
  const dispatch = useGlobalEventDispatch();

  const state = useMemo(() => {
    if (!user) return null;

    const players = [];
    const controllers = [];

    for (let i = 0; i < 3; i++) {
      const newPlayer = new Player(user.uid, i);
      players.push(newPlayer);
      controllers.push(new LivePlayerController(newPlayer.id));
    }

    return {
      players,
      controllers,
    };
  }, []);

  useEffect(() => {
    if (!gameManager || !user || !state) return;
    const startTime = getEstimatedServerTime() + COUNTDOWN_TIME_MS;
    const liveServer = new GameServer(
      "live_player_triple",
      world,
      state.players,
      state.controllers,
    );

    const removeLiveServer = gameManager.add(liveServer);
    gameManager.setPrimaryServer(liveServer.id);
    liveServer.start(startTime);

    const handleSetTabbing = (payload: SetTabbingPayload) => {
      state.controllers[payload.playerIndex]?.setTabbing(payload.isTabbing);
    };

    const handleReset = () => {
      if (!liveServer.isRoundOver && liveServer.simulation.currentStep > 0)
        submitReplayV2({
          levelId,
          userId: user.uid,
          replay: liveServer.createReplay(levelId),
        });
      liveServer.start(getEstimatedServerTime() + COUNTDOWN_TIME_MS);
    };

    const handleRoundFinished = (_result: unknown, isPrimary: boolean) => {
      if (!isPrimary) return;

      const replay = liveServer.createReplay(levelId);
      submitReplayV2({
        levelId,
        userId: user.uid,
        replay,
      }).then((res) => {
        if (res.isNewPersonalBest)
          dispatch("new-personal-best", {replay: res.replay});
      });

      const verifier = new ReplayVerifier(world);
      const res = verifier.verify(replay);
      console.log("Replay Verification Result:", replay, res);
    };

    userInputEmitter.on("set-tabbing", handleSetTabbing);
    userInputEmitter.on("reset-simulation", handleReset);
    gameManager.on("round-finished", handleRoundFinished);

    // Master cleanup function
    return () => {
      console.log("Cleanup", liveServer);
      if (!liveServer.isRoundOver)
        submitReplayV2({
          levelId,
          userId: user.uid,
          replay: liveServer.createReplay(levelId),
        }).then(console.warn);
      userInputEmitter.off("set-tabbing", handleSetTabbing);
      userInputEmitter.off("reset-simulation", handleReset);
      gameManager.off("round-finished", handleRoundFinished);
      removeLiveServer();
      gameManager.setPrimaryServer(null);
    };
  }, [
    gameManager,
    user,
    world,
    getEstimatedServerTime,
    state,
    userInputEmitter,
    levelId,
  ]);

  return null;
};

export default SinglePlayerGame;
